import { DocumentMeta } from './meta';
import { isDistributorStatement, isDirectSalesStatement } from './waterfall/statement';
import type { Statement, WaterfallSource } from './waterfall';
import type { Media, MovieCurrency, PaymentStatus, Territory } from './static/types';
import { sum } from './utils';

interface IncomeExtraData { [key: string]: number }

export interface Income extends IncomeLite {
  _meta?: DocumentMeta;
  /** TermId linked to this income */
  termId: string;
  /** The contract that created this income */
  contractId: string;
  price: number;
  version: Record<string, { price: number }>;
  currency?: MovieCurrency;
  offerId?: string;
  titleId?: string;
  status: PaymentStatus;
  date: Date;
  medias: Media[]; // For waterfall purposes, optional
  territories: Territory[]; // For waterfall purposes, optional
  order: number; // For waterfall purposes
  extra?: IncomeExtraData; // For waterfall purposes
  licensee?: string; // For waterfall purposes
}

export interface IncomeLite {
  id: string;
  sourceId: string; // Should be defined for waterfall purposes
}

export function createIncome(params: Partial<Income> = {}): Income {
  return {
    id: '',
    termId: '',
    contractId: '',
    price: 0,
    version: {},
    status: 'pending',
    date: new Date(),
    medias: [],
    territories: [],
    sourceId: '',
    order: 0,
    ...params,
  };
}

export function createIncomeLite(params: Partial<Income> = {}): IncomeLite {
  return {
    id: params.id ?? '',
    sourceId: params.sourceId ?? ''
  };
}

export function createDefaultIncome(source: WaterfallSource, statement: Statement): Income {
  return createIncome({
    titleId: statement.waterfallId,
    contractId: statement.contractId,
    date: statement.duration.to,
    sourceId: source.id,
    price: 0,
    territories: source.territories,
    medias: source.medias,
  });
}

/**
 * Return incomes with the same contractId, medias and territories
 * @param subject 
 * @param incomes 
 * @param filterBySource 
 */
export function getMatchingIncomes(subject: Income, incomes: Income[], filterBySource = false): Income[] {
  return incomes.filter(p => {
    if (filterBySource && p.sourceId !== subject.sourceId) return false;
    if ((!!subject.contractId || !!p.contractId) && p.contractId !== subject.contractId) return false;
    if ((!!subject.licensee || !!p.licensee) && p.licensee !== subject.licensee) return false;
    const sameMedias = p.medias.every(m => subject.medias.includes(m)) && p.medias.length === subject.medias.length;
    const sameTerritories = p.territories.every(t => subject.territories.includes(t)) && p.territories.length === subject.territories.length;
    return sameMedias && sameTerritories;
  });
}

/**
 * Add missing extra data attributes to current income with previously existing extra data
 * @param subject 
 * @param incomes 
 */
export function addMissingExtraData(subject: Income, incomes: Income[]) {
  const matching = getMatchingIncomes(subject, incomes);
  const extraKeys: string[] = [];
  for (const m of matching) {
    const keys = Object.keys(m.extra || {});
    if (keys.length > 0) extraKeys.push(...keys);
  }
  for (const key of Array.from(new Set(extraKeys))) {
    if (!subject.extra) subject.extra = {};
    if (!subject.extra[key]) subject.extra[key] = 0;
  }
  return subject;
}

export function mergeMatchingIncomes(_incomes: Income[], filterBySource = false): Income[] {
  const incomes: Income[] = [];
  for (const _income of _incomes) {
    const income = createIncome({ ..._income, extra: { ..._income.extra } });
    if (getMatchingIncomes(income, incomes, filterBySource).length > 0) continue; // Already processed
    const duplicates = getMatchingIncomes(income, _incomes, filterBySource);
    if (!income.id && duplicates.find(d => !!d.id)) {
      income.id = duplicates.find(d => !!d.id).id;
    }
    income.price = sum(duplicates, d => d.price);
    const extraKeys: string[] = [];
    for (const m of duplicates) {
      const keys = Object.keys(m.extra || {});
      if (keys.length > 0) extraKeys.push(...keys);
    }
    for (const key of Array.from(new Set(extraKeys))) {
      if (!income.extra) income.extra = {};
      if (!income.extra[key]) income.extra[key] = 0;
      income.extra[key] = sum(duplicates, d => d.extra[key] || 0);
    }

    incomes.push(income);
  }
  return incomes;
}

/**
 * Return incomes of a specific source
 * @param incomes 
 * @param sourceId 
 * @returns 
 */
export function getSourceIncomes(incomes: Income[], sourceId: string) {
  return incomes.filter(i => i.sourceId === sourceId);
}

/**
 * Order incomes by statement reported date, then by income order and finally by income date
 * @param incomes 
 * @param statements 
 * @returns 
 */
export function getOrderedIncomes(incomes: Income[], statements: Statement[]) {

  // Order statements by reported date
  const sortedIncomingStatements = statements.filter(s => isDistributorStatement(s) || isDirectSalesStatement(s)).sort((a, b) => {
    const aDate = a.reported ? a.reported : new Date(0);
    const bDate = b.reported ? b.reported : new Date(0);
    return aDate.getTime() - bDate.getTime();
  });

  // Order incomes by statement reported date, then by order
  const incomesWithStatements: Income[] = [];
  for (const sortedStatement of sortedIncomingStatements) {
    const statementIncomes = incomes.filter(i => sortedStatement.incomeIds.includes(i.id)).sort((a, b) => a.order - b.order);
    for (const income of statementIncomes) {
      incomesWithStatements.push(income);
    }
  }

  // Order incomes without statements by order (in case of simulation)
  const incomesWithoutStatements = incomes.filter(i => !incomesWithStatements.includes(i)).sort((a, b) => a.order - b.order);

  return [...incomesWithStatements, ...incomesWithoutStatements].sort((a, b) => a.date.getTime() - b.date.getTime());
}